import { useState, useEffect, useRef } from 'react';

/**
 * Hook to detect units decommissioned by strategem 111 (Kinetron Blast)
 * Tracks units that disappear from battlefield and appear in hand (decommissioned)
 * Returns map of decommissioned units for animation
 */
export const useDecommissionAnimations = (gameState, userId) => {
  const [decommissionedUnits, setDecommissionedUnits] = useState(new Map());
  const previousUnitsRef = useRef(new Map());
  const animationTimeoutRef = useRef(new Map());

  useEffect(() => {
    if (!gameState?.state || !userId) return;

    const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
    const currentUnits = new Map();

    // Build map of current units on battlefield
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach(zone => {
        if (player.playedOrders[zone]) {
          player.playedOrders[zone].forEach((unit, idx) => {
            if (unit && unit.instanceId) {
              const key = `${playerId}_${unit.instanceId}`;
              // Store full unit data for decommission detection
              currentUnits.set(key, {
                instanceId: unit.instanceId,
                id: unit.id,
                name: unit.name,
                zone,
                index: idx,
                playerId,
                // Store full unit object for animation
                fullUnit: { ...unit }
              });
            }
          });
        }
      });
    });

    // Detect decommissioned units (unit was on battlefield, now in hand)
    const newDecommissions = new Map();
    previousUnitsRef.current.forEach((previousUnit, key) => {
      // Check if unit is no longer on battlefield
      if (!currentUnits.has(key)) {
        // Check if strategem 111 was recently played
        let strategem111Played = false;
        for (const playerId of allPlayerIds) {
          const player = gameState.state?.[playerId];
          if (player?.completedOrders) {
            const strategem111 = player.completedOrders.find(
              order => order && order.id === 111 && (order.type === 'Strategem' || !order.type)
            );
            if (strategem111) {
              strategem111Played = true;
              break;
            }
          }
        }

        if (strategem111Played) {
          // Check if unit with same ID (but different instanceId) is in hand
          const unitOwner = gameState.state?.[previousUnit.playerId];
          if (unitOwner?.hand) {
            const unitInHand = unitOwner.hand.some(
              order => order && order.id === previousUnit.id && order.instanceId !== previousUnit.instanceId
            );
            if (unitInHand) {
              // Unit was decommissioned!
              const zonePrefix = String(previousUnit.playerId) === String(userId) ? 'your' : 'opponent';
              const zoneName = `${zonePrefix}_${previousUnit.zone}`;
              
              // Get full unit data from previousUnit.fullUnit if available
              const fullUnitData = previousUnit.fullUnit || {
                id: previousUnit.id,
                name: previousUnit.name,
                instanceId: previousUnit.instanceId
              };
              
              newDecommissions.set(key, {
                instanceId: previousUnit.instanceId,
                id: previousUnit.id,
                name: previousUnit.name,
                zone: zoneName,
                index: previousUnit.index,
                playerId: previousUnit.playerId,
                isPlayerUnit: String(previousUnit.playerId) === String(userId),
                unitData: fullUnitData // Store full unit data for overlay
              });

              // Set timeout to remove after animation completes
              const timeoutId = setTimeout(() => {
                setDecommissionedUnits(prev => {
                  const next = new Map(prev);
                  next.delete(key);
                  return next;
                });
                animationTimeoutRef.current.delete(key);
              }, 2000); // Animation duration (2 seconds)

              animationTimeoutRef.current.set(key, timeoutId);
            }
          }
        }
      }
    });

    // Update decommissioned units
    if (newDecommissions.size > 0) {
      setDecommissionedUnits(prev => {
        const next = new Map(prev);
        newDecommissions.forEach((decommission, key) => {
          next.set(key, decommission);
        });
        return next;
      });
    }

    // Update previous units reference
    previousUnitsRef.current = currentUnits;

    // Cleanup function
    return () => {
      animationTimeoutRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      animationTimeoutRef.current.clear();
    };
  }, [gameState, userId]);

  return {
    decommissionedUnits
  };
};
